import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  Typography,
  Divider,
  Tag,
  Button,
  Space,
  Input,
} from "antd";


import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CarOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text } = Typography;
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const OrderManagementReports = () => {
  const [loading, setLoading] = useState(true);
  const [orderVolume, setOrderVolume] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [cancelledAnalysis, setCancelledAnalysis] = useState([]);
  const [returnsRefunds, setReturnsRefunds] = useState([]);
  const [deliveryPerformance, setDeliveryPerformance] = useState([]);

  const [pendingSearch, setPendingSearch] = useState("");
  const [deliverySearch, setDeliverySearch] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [
        volumeRes,
        pendingRes,
        cancelledRes,
        returnsRes,
        deliveryRes,
      ] = await Promise.all([
        axios.get(`${BASEURL}/reports/order-volume`, config),
        axios.get(`${BASEURL}/reports/pending-orders`, config),
        axios.get(`${BASEURL}/reports/cancelled-analysis`, config),
        axios.get(`${BASEURL}/reports/returns-refunds`, config),
        axios.get(`${BASEURL}/reports/delivery-performance`, config),
      ]);

      setOrderVolume(volumeRes.data);
      setPendingOrders(pendingRes.data);
      setCancelledAnalysis(cancelledRes.data);
      setReturnsRefunds(returnsRes.data);
      setDeliveryPerformance(deliveryRes.data);
    } catch (error) {
      console.error("Error fetching order management reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName, columns) => {
    let exportData = data;
    if (columns) {
      exportData = data.map((item, index) => {
        const row = { "S.No": index + 1 };
        columns.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : col.render ? col.render(null, item) : "";
        });
        return row;
      });
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };


  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading Order Management Analytics..." />
      </div>
    );
  }

  const returnsData = returnsRefunds.map((item) => ({
    name: `${item._id.status || "Other"} (${item._id.refundStatus || "No Refund"})`,
    value: item.count,
    amount: item.amount,
  }));

  const downloadAllReports = () => {
    const wb = XLSX.utils.book_new();
    const sections = [
      { name: "Order Volume", data: orderVolume, cols: [{ title: "Month", dataIndex: "_id" }, { title: "Orders", dataIndex: "totalOrders" }, { title: "Revenue", dataIndex: "totalRevenue" }] },
      { name: "Pending Orders", data: pendingOrders, cols: [{ title: "Order ID", dataIndex: "orderId" }, { title: "Status", dataIndex: "orderStatus" }, { title: "Days Pending", dataIndex: "daysPending" }, { title: "Amount", dataIndex: "totalPrice" }] },
      { name: "Cancellations", data: cancelledAnalysis, cols: [{ title: "Reason", dataIndex: "_id" }, { title: "Count", dataIndex: "count" }, { title: "Lost Revenue", dataIndex: "totalLostRevenue" }] },
      { name: "Returns", data: returnsData, cols: [{ title: "Status", dataIndex: "name" }, { title: "Count", dataIndex: "value" }, { title: "Amount", dataIndex: "amount" }] },
      { name: "Delivery Performance", data: deliveryPerformance, cols: [{ title: "Name", dataIndex: "name" }, { title: "Assigned", dataIndex: "totalAssigned" }, { title: "Delivered", dataIndex: "totalDelivered" }] }
    ];

    sections.forEach(section => {
      const exportData = section.data.map((item, index) => {
        const row = { "S.No": index + 1 };
        section.cols.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : col.render ? col.render(null, item) : "";
        });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, section.name);
    });

    XLSX.writeFile(wb, "Order_Management_Full_Report.xlsx");
  };

  const filteredPending = pendingOrders.filter(o => String(o.orderId || "").toLowerCase().includes(pendingSearch.toLowerCase()));
  const filteredDelivery = deliveryPerformance.filter(d => String(d.name || "").toLowerCase().includes(deliverySearch.toLowerCase()));

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1 }}>Reports</div>
          <h2 style={{ margin: 0, fontFamily: "Outfit,sans-serif", fontSize: 22, fontWeight: 700, color: "#212121" }}>Order Management Reports</h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: 4 }}>Refresh</Button>
          <Button 
            type="primary" 
            icon={<FileExcelOutlined />} 
            onClick={downloadAllReports}
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4, fontWeight: 600 }}
          >
            Download All Reports
          </Button>
        </Space>
      </div>


      {/* Overview Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Pending Orders</span>}
              value={pendingOrders.length}
              prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Cancellations (Total)</span>}
              value={cancelledAnalysis.reduce((acc, curr) => acc + curr.count, 0)}
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "#ff4d4f", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Returns (Total)</span>}
              value={returnsRefunds.filter(r => r._id.status === "Return").reduce((acc, curr) => acc + curr.count, 0)}
              prefix={<ReloadOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Delivery Performance</span>}
              value={
                deliveryPerformance.length > 0 
                ? (deliveryPerformance.reduce((a, b) => a + b.totalDelivered, 0) / deliveryPerformance.reduce((a, b) => a + b.totalAssigned, 0) * 100).toFixed(1)
                : 0
              }
              suffix="%"
              prefix={<CarOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a", fontWeight: 700 }}
            />
          </div>
        </Col>
      </Row>

      {/* Order Volume Chart */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Order Volume Trend</span>
          <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(orderVolume, "Order_Volume_Trend", [
            { title: "Month", dataIndex: "_id" },
            { title: "Orders", dataIndex: "totalOrders" },
            { title: "Revenue (₹)", dataIndex: "totalRevenue" }
          ])} style={{ borderRadius: 4 }}>Export</Button>
        </div>
        <div style={{ padding: 20, height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={orderVolume}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="totalOrders" name="Order Count" stroke="#1890ff" strokeWidth={3} dot={{ r: 4, fill: "#1890ff" }} activeDot={{ r: 6 }} />
              <Line yAxisId="right" type="monotone" dataKey="totalRevenue" name="Revenue (₹)" stroke="#52c41a" strokeWidth={3} dot={{ r: 4, fill: "#52c41a" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Full Width Pending Orders Table */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: 24 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Pending Orders / Fulfillment</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
            <Input 
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
              placeholder="Search ID..."
              size="small"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              style={{ width: 160, borderRadius: 4 }} 
            />
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(pendingOrders, "Pending_Orders", [
              { title: "Order ID", dataIndex: "orderId" },
              { title: "Status", dataIndex: "orderStatus" },
              { title: "Days Pending", dataIndex: "daysPending" },
              { title: "Amount (₹)", dataIndex: "totalPrice" },
              { title: "Created At", dataIndex: "createdAt" }
            ])} style={{ borderRadius: 4 }}>Export</Button>
          </div>
        </div>
        <Table
          dataSource={filteredPending}
          columns={[
            { title: "S.No", key: "index", width: 60, render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span> },
            { title: "Order ID", dataIndex: "orderId", key: "orderId", render: (id) => <span style={{ fontWeight: 600 }}>{id}</span> },
            { title: "Status", dataIndex: "orderStatus", key: "status", render: (s) => <Tag color="orange" style={{ borderRadius: 50 }}>{s}</Tag> },
            { title: "Days Pending", dataIndex: "daysPending", key: "days", sorter: (a, b) => a.daysPending - b.daysPending, render: (d) => <Text type={d > 3 ? "danger" : "warning"} style={{ fontWeight: 600 }}>{d} days</Text> },
            { title: "Amount", dataIndex: "totalPrice", key: "amount", render: (a) => <span style={{ fontWeight: 700 }}>₹{a}</span> },
          ]}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
          size="middle"
          rowKey="_id"
        />
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* Return & Refund Distribution */}
        <Col xs={24} lg={12}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Return & Refund Distribution</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(returnsData, "Returns_Refunds_Report", [
                { title: "Status Type", dataIndex: "name" },
                { title: "Order Count", dataIndex: "value" },
                { title: "Amount (₹)", dataIndex: "amount" }
              ])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20, height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={returnsData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                  >
                    {returnsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>

        {/* Cancellation Analysis */}
        <Col xs={24} lg={12}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Cancellation Analysis</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(cancelledAnalysis, "Cancellation_Analysis", [
                { title: "Reason", dataIndex: "_id" },
                { title: "Count", dataIndex: "count" },
                { title: "Lost Revenue (₹)", dataIndex: "totalLostRevenue" }
              ])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cancelledAnalysis}
                      dataKey="count"
                      nameKey="_id"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                    >
                      {cancelledAnalysis.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ marginTop: 10 }}>
                {cancelledAnalysis.slice(0, 4).map((item, idx) => (
                  <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <Text style={{ fontSize: 13 }}>{item._id || "Other"}</Text>
                    <Text strong style={{ fontSize: 13 }}>{item.count}</Text>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Full Width Delivery Performance Table */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
          <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Delivery Performance</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
            <Input 
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
              placeholder="Search name..."
              size="small"
              value={deliverySearch}
              onChange={(e) => setDeliverySearch(e.target.value)}
              style={{ width: 150, borderRadius: 4 }} 
            />
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(deliveryPerformance, "Delivery_Performance", [
              { title: "Delivery Boy", dataIndex: "name" },
              { title: "Assigned", dataIndex: "totalAssigned" },
              { title: "Delivered", dataIndex: "totalDelivered" },
              { title: "Avg Days", dataIndex: "avgDeliveryTime" }
            ])} style={{ borderRadius: 4 }}>Export</Button>
          </div>
        </div>
        <Table
          dataSource={filteredDelivery}
          columns={[
            { title: "S.No", key: "index", width: 60, render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span> },
            { title: "Delivery Boy", dataIndex: "name", key: "name", render: (n) => <span style={{ fontWeight: 600 }}>{n}</span> },
            { title: "Assigned", dataIndex: "totalAssigned", key: "assigned" },
            { title: "Delivered", dataIndex: "totalDelivered", key: "delivered" },
            { 
              title: "Success Rate", 
              key: "rate", 
              render: (_, record) => <Tag color="green" style={{ borderRadius: 50 }}>{((record.totalDelivered / record.totalAssigned) * 100).toFixed(1)}%</Tag> 
            },
          ]}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
          size="middle"
          rowKey="_id"
        />
      </div>
    </div>
  );
};

export default OrderManagementReports;


